import SylndrButton from '@/components/shared/Button/button-styled.component';
import BottomSheetNoPadding from '@/components/shared/bottom-sheet/bottom-sheet-no-padding.component';
import Typography from '@/components/shared/typography/typography.component';
import { NEXT_APP_MAINTENANCE_DISABLE_BOOKING } from '@/constants';
import myImageLoader from '@/image.local.loader';
import { useAnalytics } from '@/integrations';
import { CarDetailsType } from '@/types';
import { numberWithCommas } from '@/utils';
import { TFunction } from 'i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from '@/hook';
import {
    CalendarHalfGreenCheckedIcon,
    InspectionPointsIcon,
    LegalitiesCheckedIcon,
    NoAccidentsIcon,
    NotTemperedIcon,
    ZeroRepairCostIcon,
    DashedGrayLineMobile,
} from 'public/Images';
import { FC, useEffect, useRef } from 'react';

const ItemList: FC<{
    text: string;
    value: number | string;
    t: TFunction<'translation', undefined>;
}> = ({ text, value, t }) => {
    return (
        <div className="flex justify-between items-center">
            <div className="flex gap-1.5 items-center px-2">
                <div className="w-[3px] h-[3px] bg-gray-500 rounded-full" />
                <Typography variant="caption" className="!text-gray-500">
                    {text}
                </Typography>
            </div>
            {typeof value === 'string' ? (
                <Typography variant="caption" className="!text-primary-600">
                    {value}
                </Typography>
            ) : (
                <Typography variant="caption" className="!text-primary-600">
                    {t('EGP')} {numberWithCommas(+value)}
                </Typography>
            )}
        </div>
    );
};
const BenefitItem: FC<{ benefit: string; icon: string }> = ({
    benefit,
    icon,
}) => {
    return (
        <div className="flex flex-col justify-between gap-2.5 items-center w-20">
            <div className="w-14 h-14 bg-white rounded-xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.08)] flex items-center justify-center">
                <Image src={icon} alt="icon" />
            </div>
            <Typography className="!text-[10px] !text-gray-800 font-semibold text-center h-10">
                {benefit}
            </Typography>
        </div>
    );
};

const PriceBreakdownBottomsheet: FC<{
    carDetails: CarDetailsType;
    openPriceBreakdownModal: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    t: TFunction<'translation', undefined>;
    continueButton?: boolean;
}> = ({
    carDetails,
    openPriceBreakdownModal,
    setOpen,
    t,
    continueButton = false,
}) => {
    const { i18n } = useTranslation();
    const carDisplayName = `${carDetails.make} ${carDetails.model} ${carDetails.year}`;
    const { trackEvent } = useAnalytics();
    const scrollToDiv = useRef(null);
    useEffect(() => {
        if (!openPriceBreakdownModal) {
            scrollToDiv?.current?.scrollIntoView({
                behavior: 'instant',
                block: 'start',
            });
        }
    }, [openPriceBreakdownModal]);
    return (
        <BottomSheetNoPadding open={openPriceBreakdownModal} setOpen={setOpen}>
            <div className="px-3 bg-[#FFF] bg-gradient-to-b from-[#F3FFFA] via-[#F3FFFA00]/[0%] pt-3 h-[80vh] overflow-hidden">
                <div className="bg-gray-300 h-1 w-20 rounded-[100px] mx-auto mb-3" />
                {/* Header */}

                <div className="flex gap-2 pl-2 py-4 items-center w-full ">
                    <div className="w-[53px] h-10 ">
                        <img
                            className="rounded"
                            src={myImageLoader({
                                src: carDetails.images[0],
                                width: 200,
                                height: 150,
                                quality: 80,
                            })}
                            alt="car image"
                        />
                    </div>
                    <div className="flex flex-col">
                        <Typography
                            variant="body1SemiBold"
                            className="!text-gray-800"
                        >
                            {t('priceBenefits')}
                        </Typography>
                        <Typography
                            variant="captionMedium"
                            className="!text-gray-600"
                        >
                            {carDisplayName}
                        </Typography>
                    </div>
                </div>
                <div className="py-3 flex flex-col gap-6 mb-20 overflow-y-scroll h-[74%]">
                    {/* First Container */}

                    <div
                        className="py-4 px-3 rounded-xl border border-gray-200 bg-white"
                        ref={scrollToDiv}
                    >
                        <div className="flex justify-between mb-3">
                            <Typography
                                variant="body2Medium"
                                className="!text-gray-700"
                            >
                                {t('baseCarPrice')}
                            </Typography>
                            <Typography
                                variant="body2Medium"
                                className="!text-gray-800"
                            >
                                {t('EGP')}{' '}
                                {numberWithCommas(
                                    carDetails.basePrice +
                                        carDetails.warrantyPrice || 0
                                )}
                            </Typography>
                        </div>
                        <div className="flex flex-col gap-2 pr-2">
                            <ItemList
                                text={t('ownershipFees')}
                                value={carDetails.registrationFees || 0}
                                t={t}
                            />
                            <ItemList
                                text={t('warrantyDays')}
                                value={t('included')}
                                t={t}
                            />
                            <ItemList
                                text={t('sylndrFees')}
                                value={10000}
                                t={t}
                            />
                            <ItemList
                                text={t('carRenewalIncluded')}
                                value={t('included')}
                                t={t}
                            />
                        </div>
                        <Image
                            src={DashedGrayLineMobile}
                            alt="dashed-line"
                            className="w-full my-4"
                        />
                        <div className="flex justify-between items-center">
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700"
                            >
                                {t('currentPrice')}
                            </Typography>
                            <Typography
                                variant="body2SemiBold"
                                className="!text-gray-700 "
                            >
                                {t('EGP')}{' '}
                                {numberWithCommas(+carDetails.displayingPrice)}
                            </Typography>
                        </div>
                    </div>
                    {/* Second Container */}
                    <div className="flex flex-col gap-4">
                        <div className="w-full flex items-center justify-center">
                            <Typography
                                variant="body2Medium"
                                className="!text-gray800
                                "
                            >
                                {t('whyBuyFromSylndr')}
                            </Typography>
                        </div>
                        <div className="flex flex-col gap-4">
                            <div className="flex px-6  justify-between items-center">
                                <BenefitItem
                                    benefit={t('inspectionPoints')}
                                    icon={InspectionPointsIcon}
                                />
                                <BenefitItem
                                    benefit={t('zeroRepairCost')}
                                    icon={ZeroRepairCostIcon}
                                />
                                <BenefitItem
                                    benefit={t('returnPolicyDays')}
                                    icon={CalendarHalfGreenCheckedIcon}
                                />
                            </div>
                            <div className="flex px-6 gap-6 justify-between items-center">
                                <BenefitItem
                                    benefit={t('nonMajorAccidents')}
                                    icon={NoAccidentsIcon}
                                />
                                <BenefitItem
                                    benefit={t('nonTampered')}
                                    icon={NotTemperedIcon}
                                />
                                <BenefitItem
                                    benefit={t('legalitiesChecked')}
                                    icon={LegalitiesCheckedIcon}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="py-3 px-4 left-0 !w-screen border-t-gray-200 border-t absolute bottom-0 bg-white">
                    {continueButton ? (
                        <div className="flex justify-center items-center gap-2 !w-full bg-primary-600 rounded-lg h-12 ">
                            <SylndrButton
                                disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                                onClick={() => {
                                    setOpen(false);
                                }}
                                variant="primary"
                            >
                                <Typography
                                    variant="body2Medium"
                                    className="!text-white ar:mt-1"
                                >
                                    {t('continue')}
                                </Typography>
                            </SylndrButton>
                        </div>
                    ) : (
                        <Link
                            href={`/${i18n.language}/car-details/${carDetails?.id}/checkout/see`}
                            className="flex justify-center items-center gap-2 !w-full bg-primary-600 rounded-lg h-12 "
                        >
                            <SylndrButton
                                disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                                onClick={() => {
                                    trackEvent('Action: Book Visit', {
                                        carName: carDetails?.sfVehicleName,
                                        newlyAdded:
                                            carDetails.isNewlyAdded &&
                                            !(
                                                carDetails.isReserved ||
                                                carDetails.isPurchaseInProgress
                                            ),
                                        status: carDetails.isPurchaseInProgress
                                            ? 'Being Sold'
                                            : carDetails.isReserved
                                            ? 'Reserved'
                                            : 'Not Reserved',
                                    });
                                }}
                                variant="primary"
                            >
                                <Typography
                                    variant="body2Medium"
                                    className="!text-white ar:mt-1"
                                >
                                    {t('See Car')}
                                </Typography>
                            </SylndrButton>
                        </Link>
                    )}
                </div>
            </div>
        </BottomSheetNoPadding>
    );
};

export default PriceBreakdownBottomsheet;
